import { load } from "../../common/js/localStorage"
import Bugsnag from "@bugsnag/js"
import smoothscroll from "smoothscroll-polyfill"
import { initPricingHelpers } from "./pricing"
import { segmentIdentifyUser } from "./segment"

export const LOCAL_STORAGE_KEY = "data"
export const INVALID_DOMAIN_CODE = "invalid_domain"
export const EMAIL_VALIDATION_ERROR = "email: Invalid email"
export const EMAIL_VALIDATION_CODE = "email_verification_failed"

export default ({ getInitialValues, customOfferFieldsKeysAdapter }) => ({
  // Push data store to backend API
  init() {
    const data = load(LOCAL_STORAGE_KEY)

    // Init pricing helpers with naming adapter for custom offers fields data.
    // This is needed for consistent and backward-compatible common implementation,
    // as each pipe may have differently named offers fields for the same data.
    this.customOfferFieldsKeysAdapter = customOfferFieldsKeysAdapter
    this.pricing = initPricingHelpers(customOfferFieldsKeysAdapter)

    if (data && data.values) {
      this.values = {
        // Apply initial values at first, in case if new code version
        // added new props, but user got old version in local storage
        ...getInitialValues(),
        ...data.values,
      }
    } else {
      this.values = getInitialValues()
    }

    if (!this.values?.activeOffer) {
      const pipelineOffers = pipelineConfig.offers
      const defaultOffer = pipelineOffers.find(
        (offer) => offer.customData.default_select === "true",
      )

      this.values.activeOffer = defaultOffer || pipelineOffers[0]
    }

    this.storeAnalytics()

    Alpine.effect(() => {
      this.writeToLocalStorage(this.values)
    })
  },

  storeAnalytics() {
    const analyticsData = {}

    analyticsData.domain = document.location.host

    // Add cookies - _ga, _gid, _fbc, _fbp, ...
    document.cookie.split("; ").forEach((item) => {
      const [key, value] = item.split("=")
      analyticsData[key] = value
    })

    // Add search params - fbclid, utm_*, ...
    const url = new URL(document.location)
    url.searchParams.forEach((value, key) => {
      analyticsData[key] = value
    })

    // Add FraudNet data
    analyticsData["fraud_net_session_id"] = localStorage.getItem("fraud_net_session_id") || ""

    this.values = { ...this.values, ...analyticsData }
  },

  writeToLocalStorage(data) {
    window.localStorage.setItem(
      `${pipelineConfig.pathPrefix}:${LOCAL_STORAGE_KEY}`,
      JSON.stringify({ values: data }),
    )
  },

  resetLocalStorage() {
    this.values = getInitialValues()
    this.storeAnalytics()
  },

  isMetric() {
    return this.values.measurementSystem === "metric"
  },

  isImperial() {
    return this.values.measurementSystem === "imperial"
  },

  goToLogin() {
    const loginUrl = this.values.loginUrl
    this.values = getInitialValues()
    window.location = loginUrl || pipelineConfig.loginUrl
  },

  // Saves purchased basic offer, cause during upsells the active offer is overwritten
  savePurchasedBasicOffer() {
    if (!this.values.purchasedBasicOffer) {
      this.values.purchasedBasicOffer = this.values.activeOffer
    }
  },

  // Restores purchased basic offer, cause during upsells the active offer is overwritten
  restorePurchasedBasicOffer() {
    if (this.values.purchasedBasicOffer) {
      this.values.activeOffer = this.values.purchasedBasicOffer
      this.values.purchasedBasicOffer = null
    }
  },

  isAbandonedBasket() {
    return pipelineConfig.variants[pipelineConfig.variant].isAbandonedBasket
  },

  // Saves the needed data to the DB via 'order' api
  async storeCheckoutData({ checkoutSession, isPreloaded }) {
    this.values.checkout = checkoutSession
    this.values.initiateCheckoutAt = new Date().toISOString()

    // Keep flag to distinguish preloaded session if needed (in legacy pipes)
    this.values.isCheckoutPreloaded = isPreloaded

    if (!isPreloaded) {
      this.values.visitCheckoutAt = new Date().toISOString()
      await this.push()
    }
  },

  async pull() {
    if (this.values.token) {
      const res = await fetch(pipelineConfig.orderApiUrl, {
        method: "GET",
        headers: {
          "content-type": "application/json",
          "x-token": this.values.token,
        },
      })
      const data = await res.json()

      if (res.ok) {
        // AC is treated as a separate standalone pipe
        // with it's own set of offers and discounts.
        // Historically these fields are stored from
        // the main pipe, so we need to reset them in AC.
        if (this.isAbandonedBasket()) {
          data.activeOffer = null
          data.exitIntent = false
          data.isExitIntentPopupShown = false
        }

        this.globalError = null

        Object.entries(data).forEach(([key, value]) => {
          this.values[key] = value
        })

        await segmentIdentifyUser(this.values.segmentUserId, this.values.email)

        this.writeToLocalStorage({
          ...data,
        })
      } else {
        this.globalError = data.error
      }
    }
  },

  // Push data store to backend API
  async push({ payloadExtraData, isSilentMode } = {}) {
    const apiUrl = pipelineConfig.orderApiUrl
    const options = {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        ...this.values,
        ...payloadExtraData,
      }),
    }

    if (this.values.token) {
      options.method = "PATCH"
      options.headers["x-token"] = this.values.token
    }

    try {
      this.globalError = null
      this.emailValidationError = null

      const res = await fetch(apiUrl, options)
      const data = await res.json()

      if (data.token) {
        this.values.token = data.token
      }

      if (isSilentMode) return

      if (res.ok) {
        this.globalError = null
        this.emailValidationError = null

        Object.entries(data).forEach(([key, value]) => {
          if (key !== "path") this.values[key] = value
        })

        this.values.isEmailStepCompleted = Boolean(this.values.email)

        await segmentIdentifyUser(this.values.segmentUserId, this.values.email)
      } else {
        if (
          data.code === INVALID_DOMAIN_CODE ||
          data.error === EMAIL_VALIDATION_ERROR ||
          data.code === EMAIL_VALIDATION_CODE
        ) {
          this.emailValidationError = data
        } else {
          this.globalError = data.error
        }
      }
    } catch (error) {
      if (isSilentMode) return

      this.globalError = error
      Bugsnag.notify(error)
    }

    return !this.globalError && !this.emailValidationError
  },

  // Inherited from the u-series - tracks analytics data for the page status
  async trackStatusForAnalytics(status) {
    if (status) {
      this.values.status = status
      this.push({ payloadExtraData: { path: window.location.pathname }, isSilentMode: true })
    }
  },

  getWeightToLose() {
    const { weightKg, targetWeightKg, weightLbs, targetWeightLbs } = this.values

    const validWeightKg = parseFloat(weightKg) || 0
    const validTargetWeightKg = parseFloat(targetWeightKg) || 0
    const validWeightLbs = parseFloat(weightLbs) || 0
    const validTargetWeightLbs = parseFloat(targetWeightLbs) || 0

    const isMetric = this.isMetric()

    const weightToLose = isMetric
      ? validWeightKg - validTargetWeightKg
      : validWeightLbs - validTargetWeightLbs

    return isNaN(weightToLose) ? 0 : weightToLose
  },
})
